import React, { useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom';
import { Alert, Button, IconButton } from '@mui/material';

import { AppConfig } from '../../config/appConfig';

import { IMaterial } from '../../types/material';

import { ErrorProvider } from '../../classes/errorProvider';
import { MaterialService } from '../../services/materialService';
import { UtilityService } from '../../services/utilityService';

import { Icons } from '../icons';
import Spinner from '../spinner';
import WorkingModal, { IWorkingModalProps } from '../workingModal';

import "../../styles/entity/materialList.scss";

export interface IMaterialTableProps {
	data?: IMaterial[];
	id?: number;
	material?: string;
	category?: string;
	hazardous?: string;
	status?: string;
	onStatusChanged?: (material: IMaterial) => void;
}

const MaterialTable: React.FunctionComponent<IMaterialTableProps> = ({ data, id, material, category, hazardous, status, onStatusChanged }) => {
	const [dataView, setDataView] = useState<IMaterial[]>([]);
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(AppConfig.defaultPageSize);
	const [sortField, setSortField] = useState(AppConfig.materialTableDefaultSortField);
	const [sortAscending, setSortAscending] = useState(true);
	const [isLoadingMaterials, setIsLoadingMaterials] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | React.ReactNode | undefined>(undefined);
	const [successMessage, setSuccessMessage] = useState<string | React.ReactNode | undefined>(undefined);
	const [workingModalParams, setWorkingModalParams] = useState<IWorkingModalProps | undefined>(undefined);
	const [omitPageReset, setOmitPageReset] = useState(false);

	useEffect(() => {
		if (data) {
			setIsLoadingMaterials(true);

			let newData = data.filter((item) => {
				if (id > 0 && item.id != id) {
					return false;
				}
				if (material?.trim()) {
					if (material.trim().toLowerCase() == "(blank)") {
						if(item?.material?.trim()) return false;						
					}
					else {
						if (!item?.material?.trim()?.toLowerCase()?.includes(material.trim().toLowerCase())) return false;
					}
				}
				if (category && category != "All") {
					if (category == "(Blank)") {
						if (item?.category?.trim()) return false;
					}
					else {
						if (item.category != category) return false;
					}
				}
				if (hazardous && hazardous != "All" && parseBoolean(hazardous) != item.isHazardous) {
					return false;
				}
				if (status && status != "All" && parseBoolean(status) != item.isActive) {
					return false;
				}
				return true;
			}).slice();

			if (sortField) {
				switch (sortField) {
					case "id": {
						newData.sort((a, b) => UtilityService.compareNumbers(a.id, b.id));
						break;
					}
					case "material": {
						newData.sort((a, b) => UtilityService.compareStrings(a.material, b.material));
						break;
					}
					case "category": {
						newData.sort((a, b) => UtilityService.compareStrings(a.category, b.category));
						break;
					}
					case "hazardous": {
						newData.sort((a, b) => UtilityService.compareBooleans(a.isHazardous, b.isHazardous));
						break;
					}
					case "status": {
						newData.sort((a, b) => UtilityService.compareBooleans(a.isActive, b.isActive));
						break;
					}
					default: {
						newData.sort((a, b) => UtilityService.compareStrings(a.material, b.material));
						break;
					}
				}
			}
			if (!sortAscending) newData.reverse();

			setDataView(newData);
			if (omitPageReset) { // Don't reset the page number for some things, like changing material status
				setOmitPageReset(false);
			}
			else {
				setPage(0);
			}
			setIsLoadingMaterials(false);
		}
	}, [data, id, material, category, hazardous, status, sortField, sortAscending])

	const handleHeaderClick = (field: string) => {
		if (sortField == field) {
			setSortAscending(!sortAscending);
		}
		else {
			setSortAscending(true);
			setSortField(field);
		}
	}

	const handlePageBack = () => {
		setPage(page - 1);
	}

	const handlePageForward = () => {
		setPage(page + 1);
	}

	const handleActivate = (id: number) => {
		setSuccessMessage(undefined);
		setErrorMessage(undefined);

		var material = data.find(item => item.id == id);
		setWorkingModalParams({ params: { content: <span>Activating <strong>{material?.material ?? "material"}</strong>...</span> } });
		setOmitPageReset(true);

		MaterialService.activateMaterial(id)
			.then(response => {
				onStatusChanged?.(MaterialService.mapMaterial(response.data));
				setSuccessMessage(<span><strong>{material?.material ?? "Material"}</strong> activated successfully.</span>);
				console.log('MaterialService.activateMaterial: SUCCESS', response);
			})
			.catch(error => {
				setOmitPageReset(false);
				setErrorMessage(ErrorProvider.getError(error));
				console.log('MaterialService.activateMaterial: ERROR', error);
			})
			.finally(() => {
				setWorkingModalParams(undefined);
			});
	}

	const handleDeactivate = (id: number) => {
		setSuccessMessage(undefined);
		setErrorMessage(undefined);

		var material = data.find(item => item.id == id);
		setWorkingModalParams({ params: { content: <span>Deactivating <strong>{material?.material ?? "material"}</strong>...</span> } });
		setOmitPageReset(true);

		MaterialService.deactivateMaterial(id)
			.then(response => {
				onStatusChanged?.(MaterialService.mapMaterial(response.data));
				setSuccessMessage(<span><strong>{material?.material ?? "Material"}</strong> deactivated successfully.</span>);
				console.log('MaterialService.deactivateMaterial: SUCCESS', response);
			})
			.catch(error => {
				setOmitPageReset(false);
				setErrorMessage(ErrorProvider.getError(error));
				console.log('MaterialService.deactivateMaterial: ERROR', error);
			})
			.finally(() => {
				setWorkingModalParams(undefined);
			});
	}

	return (
		<div className="hwi-entity-search-results-table">
			{successMessage &&
				<Alert className="alert-success"
					severity="success"
					action={<IconButton onClick={() => { setSuccessMessage(undefined); }} size='small'>X</IconButton>}>
					{successMessage}
				</Alert>
			}
			{errorMessage &&
				<Alert className="alert-error"
					severity="error"
					action={<IconButton onClick={() => { setErrorMessage(undefined); }} size='small'>X</IconButton>}>
					{errorMessage}
				</Alert>
			}
			<div className="headers">
				<div className="col-id">
					<p onClick={() => handleHeaderClick("id")}>
						ID
						{" "}
						{(sortField == "id" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "id" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-material">
					<p onClick={() => handleHeaderClick("material")}>
						Material
						{" "}
						{(sortField == "material" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "material" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-category">
					<p onClick={() => handleHeaderClick("category")}>
						Category
						{" "}
						{(sortField == "category" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "category" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-hazardous">
					<p onClick={() => handleHeaderClick("hazardous")}>
						Hazardous
						{" "}
						{(sortField == "hazardous" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "hazardous" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-status">
					<p onClick={() => handleHeaderClick("status")}>
						Status
						{" "}
						{(sortField == "status" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "status" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-controls">{""}</div>
			</div>
			<Spinner dependency={isLoadingMaterials} text="Loading...">
				<div className="data">
					{dataView?.length > 0 ?
						(
							dataView?.slice(page * pageSize, (page + 1) * pageSize).map((item) => (
								<div className="row" key={item.id}>
									<div className={`col-id${!item.isActive ? " inactive" : ""}`}><p>{item.id}</p></div>
									<div className={`col-material${!item.isActive || !item.material ? " inactive" : ""}`}><p>{item.material ? item.material : "(Blank)"}</p></div>
									<div className={`col-category${!item.isActive || !item.category ? " inactive" : ""}`}><p>{item.category ? item.category : "(Blank)"}</p></div>
									<div className={`col-hazardous${!item.isActive ? " inactive" : ""}`}><p>{item.isHazardous == undefined ? "(Not Set)" : item.isHazardous ? "Hazardous" : "Non-Hazardous"}</p></div>
									<div className={`col-status${!item.isActive ? " inactive" : ""}`}><p>{item.isActive == undefined ? "(Not Set)" : item.isActive ? "Active" : "Inactive"}</p></div>
									<div className="col-controls">
										<p>
											{item.isActive ? (
												<Button className="deactivate" variant="text" onClick={() => handleDeactivate(item.id)}>
													<Icons.RemoveItem fill="#272f50" height="14px"></Icons.RemoveItem>
													Deactivate
												</Button>
											) : (
												<Button className="activate" variant="text" onClick={() => handleActivate(item.id)}>
													<Icons.AddItem fill="#272f50" height="14px"></Icons.AddItem>
													Activate
												</Button>
											)}
											<NavLink to={"/material/" + item.id}>
												<Icons.EditItem fill="#272f50" height="14px"></Icons.EditItem>
												Edit
											</NavLink>
										</p>
									</div>
								</div>
							))
						) :
						(
							<div className="no-results">0 results</div>
						)
					}
				</div>
				{dataView?.length > 0 &&
					<div className="pager">
						<div className="pager-button"><Button variant="outlined" fullWidth={true} disabled={page < 1} onClick={handlePageBack}>Previous</Button></div>
						<div className="pager-text">{`${(page * pageSize) + 1} to ${((page + 1) * pageSize) <= dataView.length ? ((page + 1) * pageSize) : dataView.length} of ${dataView.length}`}</div>
						<div className="pager-button"><Button variant="outlined" fullWidth={true} disabled={((page + 1) * pageSize) >= dataView.length} onClick={handlePageForward}>Next</Button></div>
					</div>
				}
			</Spinner>
			<WorkingModal params={workingModalParams?.params}></WorkingModal>
		</div>
	)
}

export default MaterialTable;